import React, { useState, useCallback, useRef, useEffect } from 'react';
import './AddressSuggest.css';

const DADATA_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address';
const DEBOUNCE_MS = 300;

interface DaDataSuggestion {
  value: string;
  unrestricted_value: string;
}

export interface AddressSuggestProps {
  value: string;
  onChange: (address: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

export const AddressSuggest: React.FC<AddressSuggestProps> = ({
  value,
  onChange,
  placeholder = 'Начните вводить адрес',
  id = 'address-suggest',
  disabled = false,
  className = ''
}) => {
  const token = process.env.REACT_APP_DADATA_TOKEN;
  const [query, setQuery] = useState(value);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !token) {
      console.warn('AddressSuggest: REACT_APP_DADATA_TOKEN не задан — подсказки адресов отключены. Добавьте ключ в .env.local и перезапустите npm start.');
    }
  }, [token]);
  const [suggestions, setSuggestions] = useState<DaDataSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!token || !q.trim()) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(DADATA_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ query: q, count: 10 }),
      });
      const data = await res.json();
      if (!res.ok) {
        console.warn('DaData API error:', res.status, data);
        setSuggestions([]);
        return;
      }
      const list = Array.isArray(data.suggestions) ? data.suggestions : [];
      setSuggestions(list);
      setOpen(list.length > 0);
    } catch (err) {
      console.warn('DaData suggest error:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (!token) return;
    debounceRef.current = setTimeout(() => fetchSuggestions(v), DEBOUNCE_MS);
  };

  const handleSelect = (s: DaDataSuggestion) => {
    const full = s.unrestricted_value || s.value;
    setQuery(full);
    onChange(full);
    setSuggestions([]);
    setOpen(false);
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 200);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={`address-suggest ${className}`}>
      <input
        id={id}
        type="text"
        value={query}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="address-suggest__input"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? `${id}-listbox` : undefined}
        role="combobox"
      />
      {loading && (
        <span className="address-suggest__spinner" aria-hidden>
          <i className="ri-loader-4-line ri-spin" />
        </span>
      )}
      {open && suggestions.length > 0 && (
        <ul
          id={`${id}-listbox`}
          className="address-suggest__list"
          role="listbox"
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s.value}-${i}`}
              className="address-suggest__item"
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
            >
              {s.value}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressSuggest;
